import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
    Box,
    Typography,
} from '@mui/material';
import { 
    useDataProvider, 
    useNotify, 
    useRefresh,
    SimpleForm,
    ReferenceInput,
    SelectInput,
    required
} from 'react-admin';
import { PERMISSION_TEMPLATES_RESOURCE_NAME as PERMISSION_TEMPLATE_RESOURCE } from '../../permissionTemplates/permission.template.dataProvider';

interface LinkHealthieModalProps {
    open: boolean;
    onClose: () => void;
    userId: number;
    userEmail: string;
    userName: string;
    isProvider?: boolean;
    isAdmin?: boolean;
}

export const LinkHealthieModal: React.FC<LinkHealthieModalProps> = ({
    open,
    onClose,
    userId,
    userEmail,
    userName,
    isProvider = false,
    isAdmin = false,
}) => {
    const [isLinking, setIsLinking] = useState(false);
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();

    const handleSubmit = async (data: any) => {
        const permissionTemplateId = data.permission_template_id;
        
        if (!permissionTemplateId) {
            notify('Please select a permission template', { type: 'warning' });
            return;
        }

        setIsLinking(true);
        try {
            const response = await dataProvider.linkHealthie('users', {
                id: userId,
                data: { permission_template_id: permissionTemplateId },
            });

            const message = response.data?.message || `Successfully linked ${userName} to Healthie`;
            notify(message, { type: 'success' });
            onClose();
            
            // Force page reload to ensure UI updates with new healthie_id
            // This will refresh the record and hide the "Link" button
            setTimeout(() => {
                window.location.reload();
            }, 500);
        } catch (error: any) {
            const errorMessage = error.body?.message || error.message || 'Failed to link Healthie account';
            notify(errorMessage, { type: 'error' });
        } finally {
            setIsLinking(false);
        }
    };

    const handleClose = () => {
        if (!isLinking) {
            onClose();
        }
    };

    const userType = isProvider ? 'Provider' : isAdmin ? 'Admin' : 'User';

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Link Healthie Account</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Connect {userName} to Healthie</strong>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            This will connect this {userType.toLowerCase()}'s account in our system to their Healthie account, 
                            allowing them to manage patients and access Healthie features.
                        </Typography>
                    </Alert>
                    
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        <strong>Step 1:</strong> Choose what permissions they'll have in Healthie:
                    </Typography>
                    
                    <SimpleForm
                        onSubmit={handleSubmit}
                        toolbar={<div />}
                        sx={{ '& .RaSimpleForm-main': { mt: 0 } }}
                    >
                        <ReferenceInput
                            source="permission_template_id"
                            reference={PERMISSION_TEMPLATE_RESOURCE}
                        >
                            <SelectInput
                                optionText="name"
                                label="Permission Template"
                                fullWidth
                                validate={required()}
                            />
                        </ReferenceInput>
                    </SimpleForm>
                    
                    <Alert severity="info" sx={{ mt: 2, backgroundColor: '#f5f5f5' }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Step 2: What happens when you click "Link Account"</strong>
                        </Typography>
                        <Typography variant="body2" component="div">
                            We'll look for <strong>{userEmail}</strong> in Healthie:
                        </Typography>
                        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                ✅ If they already have a Healthie account, we'll connect it
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                ✅ If not, we'll create a new Healthie account for them
                            </Typography>
                            <Typography component="li" variant="body2">
                                ✅ Their Healthie permissions will be set to the template you choose above
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                            After linking, you'll see their Healthie ID and template permission on this page.
                        </Typography>
                    </Alert>
                    
                    {!isProvider && !isAdmin && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            <strong>Warning:</strong> This user is neither a provider nor an admin. 
                            Healthie linking is only supported for providers and admins.
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLinking}>
                    Cancel
                </Button>
                <Button
                    onClick={() => {
                        // Trigger form submission
                        const form = document.querySelector('form');
                        if (form) {
                            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                        }
                    }}
                    variant="contained"
                    color="primary"
                    disabled={isLinking || (!isProvider && !isAdmin)}
                >
                    {isLinking ? <CircularProgress size={24} /> : 'Link Account'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

